import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Users } from 'lucide-react';
import { useCustomersInfinite } from '@/api/customers';
import type { CustomerType } from '@/api/types';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  ListSkeleton,
} from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { formatRelative, humanize } from '@/lib/format';
import { useDebounced } from '../tickets/useDebounced';
import { CustomerFormModal } from './CustomerFormModal';
import { CUSTOMER_TYPES } from './meta';

export function CustomersPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [type, setType] = useState<CustomerType | ''>('');
  const [creating, setCreating] = useState(false);
  const debouncedQ = useDebounced(q.trim(), 300);
  const customers = useCustomersInfinite({ q: debouncedQ || undefined, type: type || undefined });
  const rows = customers.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Support"
        title="Customers"
        description="Everyone who has contacted your support team."
        actions={
          can('create', 'Customer') && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New customer
            </Button>
          )
        }
      />
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 p-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              aria-label="Search customers"
              placeholder="Search name, email, phone or user ID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            aria-label="Type"
            value={type}
            onChange={(e) => setType(e.target.value as CustomerType | '')}
            wrapperClassName="sm:w-44"
          >
            <option value="">All types</option>
            {CUSTOMER_TYPES.map((t) => (
              <option key={t} value={t}>
                {humanize(t)}
              </option>
            ))}
          </Select>
        </div>

        {customers.isPending ? (
          <ListSkeleton label="Loading customers" />
        ) : customers.error ? (
          <ErrorState error={customers.error} onRetry={() => void customers.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title={debouncedQ || type ? 'No customers match' : 'No customers yet'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50/70 text-[11px] font-semibold tracking-wider text-slate-500 uppercase">
                <tr className="border-b border-slate-100">
                  <th scope="col" className="px-4 py-3">
                    Customer
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Type
                  </th>
                  <th scope="col" className="px-4 py-3">
                    City
                  </th>
                  <th scope="col" className="px-4 py-3 text-right">
                    Tickets
                  </th>
                  <th scope="col" className="px-4 py-3 text-right">
                    Open
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Last seen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={c.name} src={c.avatarUrl} size="sm" />
                        <div className="min-w-0">
                          <Link
                            to={`/customers/${c.id}`}
                            className="hover:text-brand-700 block truncate font-medium text-slate-900"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {c.name}
                          </Link>
                          <p className="truncate text-xs text-slate-500">{c.email ?? c.phone ?? '—'}</p>
                        </div>
                        {c.tags.includes('vip') && <Badge tone="accent">VIP</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-700">{humanize(c.type)}</span>
                      {c.plan && <span className="text-slate-400"> · {c.plan}</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.city ?? '—'}</td>
                    <td className="px-4 py-3 text-right text-slate-700 tabular-nums">{c.ticketCount}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {c.openTicketCount > 0 ? (
                        <Badge tone="brand">{c.openTicketCount}</Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500">
                      {formatRelative(c.lastSeenAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {customers.hasNextPage && (
              <div className="flex justify-center border-t border-slate-100 p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  loading={customers.isFetchingNextPage}
                  onClick={() => void customers.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
      {creating && (
        <CustomerFormModal
          customer={null}
          onClose={() => setCreating(false)}
          onCreated={(c) => navigate(`/customers/${c.id}`)}
        />
      )}
    </>
  );
}
